import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  MessageSquare, Send, Mic, MicOff, X, ChevronDown, ChevronUp,
  Sparkles, Bot, User, Loader2, Check, XCircle, History,
  Trash2, Plus, Settings, Volume2, VolumeX, Maximize2, Minimize2,
  AlertCircle, CheckCircle, Clock, Zap
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// API functions
const sendChatMessage = async (sessionId, message) => {
  const res = await fetch(`${API_URL}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, message })
  });
  return res.json();
};

const confirmAction = async (sessionId, actionId, confirmed) => {
  const res = await fetch(`${API_URL}/api/chat/confirm-action`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, action_id: actionId, confirmed })
  });
  return res.json();
};

const getChatSessions = async () => {
  const res = await fetch(`${API_URL}/api/chat/sessions`);
  return res.json();
};

const getChatSession = async (sessionId) => {
  const res = await fetch(`${API_URL}/api/chat/sessions/${sessionId}`);
  return res.json();
};

const deleteChatSession = async (sessionId) => {
  const res = await fetch(`${API_URL}/api/chat/sessions/${sessionId}`, { method: 'DELETE' });
  return res.json();
};

// Message bubble component
function MessageBubble({ message, onConfirm, onReject, isLoading }) {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  
  return (
    <div className={cn(
      "flex gap-3 mb-4",
      isUser ? "flex-row-reverse" : "flex-row"
    )}>
      {/* Avatar */}
      <div className={cn(
        "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
        isUser ? "bg-[#0EA5E9]" : isSystem ? "bg-[#F59E0B]" : "bg-gradient-to-br from-[#7C3AED] to-[#0EA5E9]"
      )}>
        {isUser ? <User className="w-4 h-4 text-white" /> : 
         isSystem ? <AlertCircle className="w-4 h-4 text-white" /> :
         <Sparkles className="w-4 h-4 text-white" />}
      </div>
      
      {/* Message content */}
      <div className={cn(
        "flex-1 max-w-[80%]",
        isUser ? "text-right" : "text-left"
      )}>
        <div className={cn(
          "inline-block px-4 py-3 rounded-2xl text-sm",
          isUser 
            ? "bg-[#0EA5E9] text-white rounded-tr-sm" 
            : isSystem
              ? "bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/30 rounded-tl-sm"
              : "bg-[#1E293B] text-[#F1F5F9] rounded-tl-sm"
        )}>
          <p className="whitespace-pre-wrap">{message.content}</p>
          
          {/* Action confirmation card */}
          {message.action && !isSystem && (
            <div className="mt-3 p-3 bg-[#030712] rounded-lg border border-[#334155]">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-[#F59E0B]" />
                <span className="text-xs font-medium text-[#F59E0B]">Pending Action</span>
              </div>
              <p className="text-xs text-[#94A3B8] mb-3">{message.action.description}</p>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]">
                  {message.action.type.toUpperCase()}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {message.action.entity}
                </Badge>
              </div>
              {onConfirm && (
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    onClick={() => onConfirm(message.action.action_id)}
                    disabled={isLoading}
                    className="flex-1 bg-[#10B981] hover:bg-[#059669] text-white h-8"
                  >
                    {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                    Confirm
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onReject(message.action.action_id)}
                    disabled={isLoading}
                    className="flex-1 border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10 h-8"
                  >
                    <XCircle className="w-3 h-3 mr-1" />
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Timestamp */}
        <div className={cn(
          "text-[10px] text-[#64748B] mt-1",
          isUser ? "text-right" : "text-left"
        )}>
          {new Date(message.timestamp).toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
}

// Session list component
function SessionList({ sessions, currentSession, onSelect, onDelete, onNew }) {
  return (
    <div className="h-full flex flex-col bg-[#030712]">
      <div className="p-3 border-b border-[#1E293B] flex items-center justify-between">
        <span className="text-xs font-medium text-[#64748B] uppercase tracking-wide">Chat History</span>
        <Button size="sm" variant="ghost" onClick={onNew} className="h-7 w-7 p-0">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {sessions.map(session => (
            <div
              key={session.id}
              className={cn(
                "p-2 rounded-md cursor-pointer transition-colors group",
                currentSession === session.id 
                  ? "bg-[#0EA5E9]/20 border border-[#0EA5E9]/30" 
                  : "hover:bg-[#1E293B]"
              )}
              onClick={() => onSelect(session.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-3 h-3 text-[#64748B]" />
                  <span className="text-xs text-[#F1F5F9] truncate max-w-[120px]">
                    {session.messages?.[0]?.content?.substring(0, 30) || 'New Chat'}...
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); onDelete(session.id); }}
                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="w-3 h-3 text-[#EF4444]" />
                </Button>
              </div>
              <div className="text-[10px] text-[#64748B] mt-1">
                {new Date(session.updated_at).toLocaleDateString()}
              </div>
            </div>
          ))}
          {sessions.length === 0 && (
            <div className="text-center py-8 text-[#64748B]">
              <History className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-xs">No chat history</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// Main AI Chat component
export default function AIChat({ isOpen, onToggle, onRefreshData }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  
  // Load sessions on mount
  useEffect(() => {
    loadSessions();
  }, []);
  
  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);
  
  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);
  
  const loadSessions = async () => {
    try {
      const res = await getChatSessions();
      setSessions(res.sessions || []);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    }
  };
  
  const loadSession = async (id) => {
    try {
      const session = await getChatSession(id);
      setSessionId(id);
      setMessages(session.messages || []);
      setShowHistory(false);
    } catch (err) {
      toast.error('Failed to load session');
    }
  };
  
  const startNewSession = () => {
    setSessionId(null);
    setMessages([]);
    setShowHistory(false);
  };
  
  const handleDeleteSession = async (id) => {
    try {
      await deleteChatSession(id);
      setSessions(prev => prev.filter(s => s.id !== id));
      if (sessionId === id) {
        startNewSession();
      }
      toast.success('Session deleted');
    } catch (err) {
      toast.error('Failed to delete session');
    }
  };
  
  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    try {
      const res = await sendChatMessage(sessionId, userMessage.content);
      
      if (res.session_id && !sessionId) {
        setSessionId(res.session_id);
      }
      
      const assistantMessage = {
        role: 'assistant',
        content: res.response,
        timestamp: new Date().toISOString(),
        action: res.action
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      loadSessions(); // Refresh session list
    } catch (err) {
      toast.error('Failed to send message');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleConfirmAction = async (actionId) => {
    setActionLoading(true);
    try {
      const result = await confirmAction(sessionId, actionId, true);
      
      // Add system message about the action
      setMessages(prev => [...prev, {
        role: 'system',
        content: result.success 
          ? `✅ ${result.message}` 
          : `❌ ${result.message}`,
        timestamp: new Date().toISOString()
      }]);
      
      // Remove action from the message
      setMessages(prev => prev.map(msg => 
        msg.action?.action_id === actionId 
          ? { ...msg, action: null }
          : msg
      ));
      
      if (result.success) {
        toast.success(result.message);
        onRefreshData?.(); // Refresh data in parent components
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      toast.error('Failed to execute action');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleRejectAction = async (actionId) => {
    setActionLoading(true);
    try {
      await confirmAction(sessionId, actionId, false);
      
      // Remove action from the message
      setMessages(prev => prev.map(msg => 
        msg.action?.action_id === actionId 
          ? { ...msg, action: null }
          : msg
      ));
      
      toast.info('Action cancelled');
    } catch (err) {
      toast.error('Failed to cancel action');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Voice input not supported in this browser');
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    
    recognition.onstart = () => {
      setIsRecording(true);
    };
    
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(prev => prev + transcript);
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      toast.error('Voice recognition failed');
    };
    
    recognition.onend = () => {
      setIsRecording(false);
    };
    
    if (isRecording) {
      recognition.stop();
    } else {
      recognition.start();
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  // Collapsed state - just a floating button
  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#0EA5E9] shadow-lg shadow-[#0EA5E9]/20 flex items-center justify-center hover:scale-110 transition-transform z-50"
        data-testid="ai-chat-toggle"
      >
        <Sparkles className="w-6 h-6 text-white" />
      </button>
    );
  }
  
  return (
    <div 
      className={cn(
        "fixed z-50 bg-[#0F172A] border border-[#1E293B] shadow-2xl shadow-black/50 flex flex-col transition-all duration-300",
        isExpanded 
          ? "inset-4 rounded-2xl" 
          : "bottom-6 right-6 w-[420px] h-[600px] rounded-2xl"
      )}
      data-testid="ai-chat-panel"
    >
      {/* Header */}
      <div className="h-14 border-b border-[#1E293B] flex items-center justify-between px-4 shrink-0 bg-gradient-to-r from-[#7C3AED]/10 to-[#0EA5E9]/10">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#0EA5E9] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-[#F1F5F9]">AI Assistant</h3>
            <p className="text-[10px] text-[#64748B]">Powered by Claude Sonnet</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowHistory(!showHistory)}
            className="h-8 w-8 p-0"
            title="Chat History"
          >
            <History className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-8 w-8 p-0"
            title={isExpanded ? "Minimize" : "Maximize"}
          >
            {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onToggle}
            className="h-8 w-8 p-0"
            title="Close"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Content area */}
      <div className="flex-1 flex overflow-hidden">
        {/* History sidebar */}
        {showHistory && (
          <div className="w-48 border-r border-[#1E293B] shrink-0">
            <SessionList
              sessions={sessions}
              currentSession={sessionId}
              onSelect={loadSession}
              onDelete={handleDeleteSession}
              onNew={startNewSession}
            />
          </div>
        )}
        
        {/* Chat area */}
        <div className="flex-1 flex flex-col">
          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#7C3AED]/20 to-[#0EA5E9]/20 flex items-center justify-center mb-4">
                  <Sparkles className="w-8 h-8 text-[#0EA5E9]" />
                </div>
                <h4 className="text-sm font-medium text-[#F1F5F9] mb-2">How can I help you?</h4>
                <p className="text-xs text-[#64748B] max-w-[280px]">
                  I can create, update, and delete data across all your tabs. 
                  Try asking me to create a new concept or update a few-shot example.
                </p>
                
                {/* Quick actions */}
                <div className="mt-6 space-y-2 w-full max-w-[280px]">
                  <p className="text-[10px] text-[#64748B] uppercase tracking-wide mb-2">Quick Actions</p>
                  {[
                    "Create a new metric called 'Daily Active Users'",
                    "Show me all few-shot examples",
                    "Update the MRR concept description"
                  ].map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(suggestion)}
                      className="w-full text-left px-3 py-2 text-xs text-[#94A3B8] bg-[#1E293B]/50 rounded-lg hover:bg-[#1E293B] transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                {messages.map((msg, i) => (
                  <MessageBubble
                    key={i}
                    message={msg}
                    onConfirm={msg.action ? handleConfirmAction : null}
                    onReject={msg.action ? handleRejectAction : null}
                    isLoading={actionLoading}
                  />
                ))}
                {isLoading && (
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#0EA5E9] flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex items-center gap-2 px-4 py-3 bg-[#1E293B] rounded-2xl rounded-tl-sm">
                      <Loader2 className="w-4 h-4 animate-spin text-[#0EA5E9]" />
                      <span className="text-sm text-[#64748B]">Thinking...</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
          
          {/* Input area */}
          <div className="p-4 border-t border-[#1E293B] bg-[#030712]/50">
            <div className="flex items-end gap-2">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me anything..."
                  className="w-full px-4 py-3 bg-[#1E293B] border border-[#334155] rounded-xl text-sm text-[#F1F5F9] placeholder:text-[#64748B] resize-none focus:outline-none focus:border-[#0EA5E9] transition-colors"
                  rows={1}
                  style={{ minHeight: '44px', maxHeight: '120px' }}
                  data-testid="ai-chat-input"
                />
              </div>
              
              {/* Voice button */}
              <Button
                size="sm"
                variant="outline"
                onClick={handleVoiceInput}
                className={cn(
                  "h-11 w-11 p-0 rounded-xl border-[#334155]",
                  isRecording && "bg-[#EF4444]/20 border-[#EF4444] text-[#EF4444]"
                )}
                title={isRecording ? "Stop recording" : "Voice input"}
              >
                {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
              
              {/* Send button */}
              <Button
                size="sm"
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className="h-11 w-11 p-0 rounded-xl bg-gradient-to-r from-[#7C3AED] to-[#0EA5E9] hover:opacity-90"
                data-testid="ai-chat-send"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            
            {/* Recording indicator */}
            {isRecording && (
              <div className="mt-2 flex items-center gap-2 text-[#EF4444]">
                <div className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" />
                <span className="text-xs">Recording...</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
